import React, { useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"

// Generic V2 combobox: filterable, async-fetchable, keyboard-navigable.
// Renders the control only (no label) so it drops straight into a fieldRow.

// Seuil d'ouverture AU FOCUS (décision Jordan 12.08.2026) : un champ déjà
// rempli d'au moins 3 caractères montre ses suggestions dès le focus —
// indépendant de minChars, qui reste le seuil de la frappe par champ.
const FOCUS_MIN_CHARS = 3
const styles = {
	root: { position: "relative", flex: 1 },
	input: { ...gs.input, width: "100%" },
	syncBox: {
		...gs.input,
		width: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: COLORS.textMuted,
	},
	dropdown: {
		position: "absolute",
		top: "calc(100% + 4px)",
		left: 0,
		right: 0,
		zIndex: 60,
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		boxShadow: "0 10px 30px rgba(28, 25, 23, 0.12)",
		padding: 6,
		maxHeight: 240,
		overflowY: "auto",
	},
	option: (active) => ({
		padding: "8px 10px",
		borderRadius: 7,
		fontSize: 13,
		cursor: "pointer",
		color: active ? COLORS.accent : COLORS.text,
		backgroundColor: active ? COLORS.accentLight : "transparent",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	}),
	empty: { padding: "10px", fontSize: 13, color: COLORS.textLight },
	adornment: {
		position: "absolute",
		right: 10,
		top: "50%",
		transform: "translateY(-50%)",
		display: "flex",
		alignItems: "center",
		pointerEvents: "none",
	},
}

const FilteredSelectV2 = ({
	options = [],
	onSelect = () => {},
	onChange = () => {},
	fetch = () => {},
	placeholder = "Rechercher…",
	minChars = 1,
	defaultKey = "label",
	optionKeys = "label",
	inputRef,
	nextRef = null,
	value = "",
	disabled = false,
	isFetching = false,
	adornment = null,
	// Overrides de style de l'input (ex. harmoniser hauteur/typo avec les
	// champs voisins d'un formulaire qui n'utilise pas gs.input).
	inputStyle = null,
}) => {
	const [search, setSearch] = useState(value)
	const [open, setOpen] = useState(false)
	const [active, setActive] = useState(-1)
	const dropdownRef = useRef(null)
	const optionRefs = useRef([])

	const filtered = useMemo(() => {
		const q = search.toLowerCase()
		return options.filter((o) =>
			(o[defaultKey] || "").toLowerCase().includes(q),
		)
	}, [search, options, defaultKey])

	useEffect(() => {
		setActive(-1)
	}, [filtered])

	useEffect(() => {
		if (active >= 0 && optionRefs.current[active]) {
			optionRefs.current[active].scrollIntoView({ block: "nearest" })
		}
	}, [active])

	useEffect(() => {
		if (value !== search) setSearch(value)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value])

	const select = (option) => {
		setSearch(option ? option[defaultKey] : "")
		setOpen(false)
		setActive(-1)
		onSelect(option)
		if (nextRef && nextRef.current) nextRef.current.focus()
	}

	const onInput = (e) => {
		const v = e.target.value
		setSearch(v)
		setOpen(v.length >= minChars)
		if (v.length >= minChars) fetch(v)
		setActive(-1)
		onChange(v)
	}

	const onKeyDown = (e) => {
		// Échap liste ouverte (même sur « Aucun résultat », cas du texte libre)
		// : ferme la liste et marque l'événement consommé — la fiche dépannage
		// ne navigue alors pas. Liste fermée : laissé à la page.
		if (e.key === "Escape") {
			if (open) {
				e.preventDefault()
				setOpen(false)
				setActive(-1)
			}
			return
		}
		if (!open || filtered.length === 0) {
			if (e.key === "ArrowDown" || e.key === "ArrowUp") {
				e.preventDefault()
				setOpen(true)
				setActive(0)
			} else if (e.key === "Enter") {
				e.preventDefault()
				if (nextRef && nextRef.current) nextRef.current.focus()
			}
			return
		}
		switch (e.key) {
			case "ArrowDown":
				e.preventDefault()
				setActive((p) => (p < filtered.length - 1 ? p + 1 : 0))
				break
			case "ArrowUp":
				e.preventDefault()
				setActive((p) => (p > 0 ? p - 1 : filtered.length - 1))
				break
			case "Enter":
				e.preventDefault()
				if (active >= 0 && filtered[active]) {
					select(filtered[active])
				} else {
					// Liste ouverte sans surlignage : Enter reprend le
					// chaînage clavier (champ suivant) au lieu d'être une
					// touche morte — indispensable depuis l'ouverture au
					// focus, chaque champ pré-rempli passait par ce cas.
					setOpen(false)
					setActive(-1)
					if (nextRef && nextRef.current) nextRef.current.focus()
				}
				break
			case "Tab":
				setOpen(false)
				setActive(-1)
				break
			default:
				setActive(-1)
				break
		}
	}

	const onFocus = (e) => {
		e.target.style.borderColor = COLORS.accent
		e.target.style.boxShadow = `0 0 0 3px ${COLORS.accentLight}`
		// Re-fetch au focus : sans lui, la liste s'ouvrait sur les résultats
		// périmés de la dernière recherche du store — cas typique du champ
		// pré-rempli par « Utiliser cette adresse », jamais fetché.
		if (search.length >= FOCUS_MIN_CHARS) {
			fetch(search)
			setOpen(true)
		}
	}

	const onBlur = (e) => {
		e.target.style.borderColor = COLORS.border
		e.target.style.boxShadow = "none"
		if (!(
			dropdownRef.current && dropdownRef.current.contains(e.relatedTarget)
		)) {
			setTimeout(() => {
				setOpen(false)
				setActive(-1)
			}, 150)
		}
	}

	const optionLabel = (o) =>
		typeof optionKeys === "string"
			? o[optionKeys]
			: optionKeys.map((k) => o[k]).join(" · ")

	return (
		<div style={styles.root}>
			{isFetching ? (
				<div style={styles.syncBox}>
					<FontAwesomeIcon icon={faSync} spin />
				</div>
			) : (
				<input
					ref={inputRef}
					type="text"
					value={search}
					onChange={onInput}
					onKeyDown={onKeyDown}
					onFocus={onFocus}
					onBlur={onBlur}
					placeholder={placeholder}
					autoComplete="off"
					role="combobox"
					aria-expanded={open}
					disabled={disabled}
					style={
						adornment
							? {
									...styles.input,
									...inputStyle,
									paddingRight: 26,
								}
							: { ...styles.input, ...inputStyle }
					}
				/>
			)}
			{adornment && !isFetching && (
				<div style={styles.adornment}>{adornment}</div>
			)}
			{open && !isFetching && (
				<div ref={dropdownRef} style={styles.dropdown} role="listbox">
					{filtered.length > 0 ? (
						filtered.map((o, i) => (
							<div
								key={o.id != null ? `o-${o.id}` : `o-${i}`}
								ref={(el) => (optionRefs.current[i] = el)}
								style={styles.option(active === i)}
								role="option"
								aria-selected={active === i}
								onMouseDown={() => select(o)}
								onMouseEnter={() => setActive(i)}
							>
								{optionLabel(o)}
							</div>
						))
					) : search.length < minChars ? (
						<div
							style={styles.empty}
						>{`Tapez au moins ${minChars} caractères`}</div>
					) : (
						<div style={styles.empty}>Aucun résultat</div>
					)}
				</div>
			)}
		</div>
	)
}

export default FilteredSelectV2
